import log from 'loglevel'
import type { LogLevelDesc } from 'loglevel'

// 環境変数に基づいてログレベルを設定
// Viteプロジェクトでは import.meta.env.MODE を使用
// NODE_ENV は Vite ではデフォルトで利用できないため、Vite の環境変数を使用
const isDevelopment = import.meta.env.MODE === 'development'

// ログ無効化設定
// VITE_DISABLE_LOGS=true で任意の環境でログを無効化
// VITE_DISABLE_LOGS=false で無効化を無効にできる（緊急時の手段）
const isLoggingDisabled = import.meta.env.VITE_DISABLE_LOGS === 'true'

// URLパラメータでログレベルを上書きできるようにする（ログ無効化時は除く）
function getLogLevelFromUrl(): LogLevelDesc | null {
  if (typeof window === 'undefined' || isLoggingDisabled) return null

  const urlParams = new URLSearchParams(window.location.search)
  const logLevel = urlParams.get('logLevel')

  if (logLevel) {
    switch (logLevel.toLowerCase()) {
      case 'trace':
        return 'trace'
      case 'debug':
        return 'debug'
      case 'info':
        return 'info'
      case 'warn':
        return 'warn'
      case 'error':
        return 'error'
      default:
        return null
    }
  }

  return null
}

// ログレベルの決定
const urlLogLevel = getLogLevelFromUrl()
let defaultLogLevel: LogLevelDesc

if (isLoggingDisabled) {
  // ログ無効化の場合、silentレベルを設定
  defaultLogLevel = 'silent'
} else if (isDevelopment) {
  defaultLogLevel = 'debug'
} else {
  // ステージング環境など
  defaultLogLevel = 'warn'
}

log.setLevel(urlLogLevel !== null ? urlLogLevel : defaultLogLevel)

// 安全なログ出力のためのラッパー関数
function safeStringify(value: unknown): string {
  if (value === null || value === undefined) return String(value)
  if (typeof value === 'symbol') return value.toString()
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value)
    } catch (_error) {
      return String(value)
    }
  }
  return String(value)
}

// Sentry統合のための遅延ロード
let sentryLogger: typeof import('@/config/sentry').sentryLogger | null = null
const getSentryLogger = async () => {
  if (!sentryLogger) {
    try {
      const { sentryLogger: sl } = await import('@/config/sentry')
      sentryLogger = sl
    } catch (error) {
      // Sentryが利用できない場合はnullのまま
      // プロダクション環境ではconsole.warnも無効化されているはず
      if (!isLoggingDisabled) {
        console.warn('Sentry logger not available:', error)
      }
    }
  }
  return sentryLogger
}

// no-op関数（プロダクション環境用）
const noOp = () => {}

// コンポーネント名を指定できるようにファクトリを拡張
const originalFactory = log.methodFactory
log.methodFactory = function (methodName, logLevel, loggerName) {
  // ログ無効化の場合、no-op関数を返す
  if (isLoggingDisabled) {
    return noOp
  }

  const rawMethod = originalFactory(methodName, logLevel, loggerName)
  return function (message, ...args) {
    const methodNameStr = safeStringify(methodName).toUpperCase()
    const loggerNameStr = loggerName ? safeStringify(loggerName) : ''
    const prefix = loggerNameStr ? `[${methodNameStr}:${loggerNameStr}]` : `[${methodNameStr}]`

    // 元のログ出力
    rawMethod(`${prefix} ${safeStringify(message)}`, ...args)

    // Sentry統合（非同期で実行）
    getSentryLogger()
      .then(sentry => {
        if (sentry) {
          const logData = args.length > 0 ? { args } : undefined

          switch (methodName) {
            case 'debug': {
              // デバッグレベルはブレッドクラムのみ
              sentry.info(`${prefix} ${safeStringify(message)}`, logData)
              break
            }
            case 'info': {
              sentry.info(`${prefix} ${safeStringify(message)}`, logData)
              break
            }
            case 'warn': {
              sentry.warn(`${prefix} ${safeStringify(message)}`, {
                ...logData,
                important: true, // 警告は重要として扱う
              })
              break
            }
            case 'error': {
              // エラーの場合は第一引数がErrorオブジェクトかチェック
              const firstArg = args[0]
              const error = firstArg instanceof Error ? firstArg : undefined
              const extraData = error ? { args: args.slice(1) } : logData

              sentry.error(`${prefix} ${safeStringify(message)}`, error, extraData)
              break
            }
          }
        }
      })
      .catch(() => {
        // Sentry統合エラーは無視（ログ出力は継続）
      })
  }
}

// 設定を再適用
log.setLevel(log.getLevel())

// 名前付きロガーを作成する関数を追加
export function getLogger(name: string) {
  if (isLoggingDisabled) {
    // ログ無効化されたロガーを返す
    return {
      trace: noOp,
      debug: noOp,
      info: noOp,
      warn: noOp,
      error: noOp,
      setLevel: noOp,
      getLevel: () => 'silent' as LogLevelDesc,
      methodFactory: () => noOp,
    }
  }
  return log.getLogger(name)
}

// Sentry統合版のロガー関数を作成
export const enhancedLogger = {
  debug: (message: string, ...args: unknown[]) => {
    if (!isLoggingDisabled) {
      log.debug(message, ...args)
    }
  },

  info: (message: string, ...args: unknown[]) => {
    if (!isLoggingDisabled) {
      log.info(message, ...args)
    }
  },

  warn: (message: string, ...args: unknown[]) => {
    if (!isLoggingDisabled) {
      log.warn(message, ...args)
    }
  },

  error: (message: string, error?: Error | unknown, ...args: unknown[]) => {
    if (!isLoggingDisabled) {
      if (error instanceof Error) {
        log.error(message, error, ...args)
      } else {
        log.error(message, error, ...args)
      }
    }
    // エラーの場合はSentryには送信（ログ無効化時も）
    getSentryLogger().then(sentry => {
      if (sentry) {
        const logData = args.length > 0 ? { args } : undefined
        const errorObj = error instanceof Error ? error : undefined
        const extraData = errorObj ? { args: args.slice(1) } : logData
        sentry.error(`[ERROR] ${safeStringify(message)}`, errorObj, extraData)
      }
    })
  },

  // API専用のログ関数
  apiRequest: (method: string, url: string, data?: unknown) => {
    // Sentryには送信するが、コンソールログは環境に応じて制御
    getSentryLogger().then(sentry => {
      if (sentry) {
        sentry.info(`API Request: ${method} ${url}`, {
          method,
          url,
          data: data ? safeStringify(data) : undefined,
        })
      }
    })
    if (!isLoggingDisabled) {
      log.debug(`API Request: ${method} ${url}`, data)
    }
  },

  apiResponse: (method: string, url: string, status: number, data?: unknown) => {
    getSentryLogger().then(sentry => {
      if (sentry) {
        sentry.info(`API Response: ${method} ${url} ${status}`, {
          method,
          url,
          status,
          data: data ? safeStringify(data) : undefined,
        })
      }
    })
    if (!isLoggingDisabled) {
      log.debug(`API Response: ${method} ${url} ${status}`, data)
    }
  },

  apiError: (method: string, url: string, status: number, error: Error | string) => {
    // APIエラーは重要なのでSentryには必ず送信
    getSentryLogger().then(sentry => {
      if (sentry) {
        sentry.apiError(url, method, status, error)
      }
    })
    if (!isLoggingDisabled) {
      log.error(`API Error: ${method} ${url} ${status}`, error)
    }
  },

  // ユーザーアクション記録
  userAction: (action: string, details?: Record<string, unknown>) => {
    getSentryLogger().then(sentry => {
      if (sentry) {
        sentry.userAction(action, details)
      }
    })
    if (!isLoggingDisabled) {
      log.info(`User Action: ${action}`, details)
    }
  },

  // パフォーマンス記録
  performance: (operation: string, duration: number, details?: Record<string, unknown>) => {
    getSentryLogger().then(sentry => {
      if (sentry) {
        sentry.performance(operation, duration, details)
      }
    })

    if (!isLoggingDisabled) {
      if (duration > 1000) {
        log.warn(`Slow operation: ${operation} took ${duration}ms`, details)
      } else {
        log.debug(`Performance: ${operation} took ${duration}ms`, details)
      }
    }
  },
}

// 設定済みのロガーインスタンスをエクスポート
export const logger = log

// ログ無効化状態をエクスポート（デバッグ用）
export { isLoggingDisabled }
